/**
 * The three fixed subscription tiers. Shared source of truth for pricing,
 * quotas (radius cap, search quota, filter access) and tier identity — consumed
 * by the platform (display + client-side gating) and the landing (pricing grid).
 * The server enforces the same limits; these values mirror them so the UI can
 * lock controls and pre-empt 409/422 before a request.
 */

export type TierCode = 'decouverte' | 'veille' | 'prospection'

/** How much of the filter catalogue a tier may use. */
export type FilterAccess = 'none' | 'type' | 'all'

export interface TierMeta {
  code: TierCode
  name: string
  /** Monthly price in cents (0 for the free tier). */
  priceCents: number
  paid: boolean
  /** Max concurrent searches allowed by the tier. */
  searchesMax: number
  /** Max search radius in km. */
  radiusMaxKm: number
  /** Filter gating: none = radius only, type = type_batiment only, all = full catalogue. */
  filterAccess: FilterAccess
  /** Export CSV des résultats (réservé aux paliers payants). */
  csvExport: boolean
  tagline: string
  features: string[]
  recommended: boolean
}

export const TIERS: Record<TierCode, TierMeta> = {
  decouverte: {
    code: 'decouverte',
    name: 'Découverte',
    priceCents: 0,
    paid: false,
    searchesMax: 1,
    radiusMaxKm: 15,
    filterAccess: 'none',
    csvExport: false,
    tagline: '3 biens par jour, pour tester le signal',
    features: [
      '3 biens révélés par jour — le reste du rayon reste masqué',
      '1 zone',
      'Rayon jusqu’à 15 km',
      'Worklist quotidien par email',
    ],
    recommended: false,
  },
  veille: {
    code: 'veille',
    name: 'Veille',
    priceCents: 990,
    paid: true,
    searchesMax: 1,
    radiusMaxKm: 15,
    filterAccess: 'type',
    csvExport: true,
    tagline: 'Tous les biens de votre rayon',
    features: [
      'Tous les biens détectés dans votre rayon',
      '1 zone',
      'Rayon jusqu’à 15 km',
      'Filtre type de bien',
      'Récap hebdomadaire + worklist quotidien',
      'Veille maintenue active',
      'Export CSV des résultats',
    ],
    recommended: false,
  },
  prospection: {
    code: 'prospection',
    name: 'Prospection',
    priceCents: 2490,
    paid: true,
    searchesMax: 5,
    radiusMaxKm: 25,
    filterAccess: 'all',
    csvExport: true,
    tagline: 'Tous les biens, jusqu’à 5 zones',
    features: [
      'Tous les biens détectés dans votre rayon',
      'Jusqu’à 5 zones',
      'Rayon jusqu’à 25 km',
      'Tous les filtres',
      'Priorité aux ventes probables',
      'Récap hebdomadaire + worklist quotidien',
      'Export CSV des résultats',
    ],
    recommended: true,
  },
}

/** Tiers in display order (free → paid). */
export const TIER_LIST: TierMeta[] = [TIERS.decouverte, TIERS.veille, TIERS.prospection]

/**
 * Période d'essai offerte (en jours) au démarrage d'un abonnement payant.
 * Appliquée par Stripe côté serveur ; mirroir ici pour l'affichage. Ne concerne
 * pas la formule Découverte (gratuite, sans essai).
 */
export const TRIAL_DAYS = 7

const priceWithCents = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Format a tier price for display, e.g. "9,90 €" or "Gratuit". */
export function formatTierPrice(priceCents: number): string {
  if (priceCents === 0) return 'Gratuit'
  return priceWithCents.format(priceCents / 100)
}
